import "react-toastify/dist/ReactToastify.css";
import {
  setError,
  setLoading,
  setAuthData,
  setUserData,
  setRoles,
  setAllUsers,
  addUser,
  updateUserInList,
  removeUser,
  setPasswordResetLoading,
  setPasswordResetError,
  setProfileUpdateLoading,
  setProfileUpdateError,
  setSessionExpiry,
  setRefreshAttempts,
  setSessionExpired,
  setInitialized,
  clearAuth
} from "../slices/authSlice";
import { 
  loginUser,
  getCurrentUser,
  validateToken,
  // logoutUser,
  refreshToken,
  getUserRoles,
  getAllUsers,
  registerUser,
  updateUserRole,
  toggleUserStatus,
  requestPasswordReset,
  resetPassword,
  changePassword,
  updateUserProfile
} from "../../utils/authAPI";
import { 
  setAuthToken, 
  setAuthUser, 
  clearAuth as clearAuthCookies,
  updateActivity 
} from "../../utils/authUtils";
import { getAuthToken, getAuthUser } from "../../utils/authUtils";
import { getClientList } from "../../utils/clientAPI";
import { selectClientList, setAvailableClients, setCurrentClient } from "../slices/clientSlice";

// Helper function to get token expiry from JWT
const getTokenExpiry = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null; // Convert to milliseconds
  } catch (error) {
    console.warn('Failed to parse token expiry:', error);
    return null;
  }
};

// Login action
export const loginAction = (credentials) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    dispatch(setError(null));
    
    const response = await loginUser(credentials);
    
    if (response.success) {
      const { user, token } = response.data;
      
      // Store in secure cookies
      setAuthToken(token, credentials.rememberMe || false);
      setAuthUser(user, credentials.rememberMe || false);
      
      // Update activity
      updateActivity();
      
      // Get token expiry
      const tokenExpiry = getTokenExpiry(token);
      
      // Dispatch to Redux
      dispatch(setAuthData({
        token,
        user,
        rememberMe: credentials.rememberMe || false
      }));
      dispatch(setSessionExpiry(tokenExpiry));
      const clientList = await getClientList( user, {});
      if (clientList.success) {
        dispatch(setAvailableClients(clientList.data));
        dispatch(setCurrentClient(clientList.data[0]));
      } else {
        dispatch(setAvailableClients([]));
        dispatch(setCurrentClient(null));
        throw new Error(clientList.message || 'Failed to get client list');
      }
      console.log("Client list fetched successfully", clientList);
      console.log("Login successful", { 
        userId: user.id || user._id,
        username: user.username,
        role: user.role,
        client_id: user.client_id || "superAdmin"
      });
      
      return response;
    } else {
      throw new Error(response.message || 'Login failed');
    }
  } catch (error) {
    console.error("Login failed:", error);
    dispatch(setError(error.message || 'Login failed'));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Get current user action
export const getCurrentUserAction = (clientId = null, user = null) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    dispatch(setError(null));
    
    const response = await getCurrentUser(clientId, user);
    
    if (response.success) {
      const user = response.data;
      
      // Update activity
      updateActivity();
      
      // Dispatch to Redux
      dispatch(setUserData(user));
      
      console.log("Current user fetched successfully", { 
        userId: user.id || user._id,
        username: user.username,
        role: user.role
      });
      
      return response;
    } else {
      throw new Error(response.message || 'Failed to get current user');
    }
  } catch (error) {
    console.error("Failed to get current user:", error);
    dispatch(setError(error.message || 'Failed to get current user'));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Validate token action
export const validateTokenAction = (clientId = null, user = null) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    dispatch(setError(null));
    
    const response = await validateToken(clientId, user);
    
    if (response.success) {
      const user = response.data;
      
      // Update activity
      updateActivity();
      
      // Dispatch to Redux
      dispatch(setUserData(user));
      
      console.log("Token validation successful", { 
        userId: user.id || user._id,
        username: user.username
      });
      
      return response;
    } else {
      throw new Error(response.message || 'Token validation failed');
    }
  } catch (error) {
    console.error("Token validation failed:", error);
    dispatch(setError(error.message || 'Token validation failed'));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Logout action
export const logoutAction = (clientId = null, user = null) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    dispatch(setError(null));
    
    // // Call logout API if available
    // const response = await logoutUser(clientId, user);
    
    // if (response.success) {
    //   console.log("✅ Logout API call successful");
    // } else {
    //   console.warn("⚠️ Logout API call failed, but continuing with local logout");
    // }
    
    // Clear local auth data
    clearAuthCookies();
    
    // Clear Redux state
    dispatch(clearAuth());
    
    console.log("✅ Logout successful");
    return { success: true };
  } catch (error) {
    console.error("❌ Logout failed:", error);
    dispatch(setError(error.message || 'Logout failed'));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Refresh token action
export const refreshTokenAction = (clientId = null, user = null, refreshAttempts = 0, rememberMe = false) => async (dispatch) => {
  try {
    console.log("🔄 Refreshing token...", {
      clientId,
      user,
      refreshAttempts,
      rememberMe
    });    
    // Check if we should attempt refresh
    if (refreshAttempts >= 3) {
      throw new Error('Max refresh attempts exceeded');
    }
    
    dispatch(setRefreshAttempts(refreshAttempts + 1));
    
    const response = await refreshToken(clientId, user);
    
    if (response.success) {
      const { token, user } = response.data;
      
      // Store in secure cookies
      setAuthToken(token, rememberMe);
      setAuthUser(user, rememberMe);
      
      // Update activity
      updateActivity();
      
      // Get token expiry
      const tokenExpiry = getTokenExpiry(token);
      
      // Dispatch to Redux
      dispatch(setAuthData({
        token,
        user,
        rememberMe: rememberMe
      }));
      dispatch(setSessionExpiry(tokenExpiry));
      dispatch(setRefreshAttempts(0));
      dispatch(setSessionExpired(false));
      
      console.log("Token refreshed successfully");
      return response;
    } else {
      throw new Error(response.message || 'Token refresh failed');
    }
  } catch (error) {
    console.error("Token refresh failed:", error);
    dispatch(setError(error.message || 'Token refresh failed'));
    
    // If max refresh attempts exceeded, mark session as expired
    if (refreshAttempts >= 3) {
      dispatch(setSessionExpired(true));
    }
    
    throw error;
  }
};

// Get user roles action
export const getUserRolesAction = (clientId = null, user = null) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    dispatch(setError(null));
    
    const response = await getUserRoles(clientId, user);
    
    if (response.success) {
      const roles = response.data;
      
      dispatch(setRoles(roles));
      
      console.log("User roles fetched successfully", { 
        roleCount: Object.keys(roles).length,
        roles: Object.keys(roles)
      });
      
      return response;
    } else {
      throw new Error(response.message || 'Failed to get user roles');
    }
  } catch (error) {
    console.error("Failed to get user roles:", error);
    dispatch(setError(error.message || 'Failed to get user roles'));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Get all users action (admin only)
export const getAllUsersAction = (clientId = null, user = null) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    dispatch(setError(null));
    
    const response = await getAllUsers(clientId, user);
    
    if (response.success) {
      const users = response.data;
      
      dispatch(setAllUsers(users));
      
      console.log("All users fetched successfully", { 
        userCount: users.length
      });
      
      return response;
    } else {
      throw new Error(response.message || 'Failed to get users');
    }
  } catch (error) {
    console.error("Failed to get users:", error);
    dispatch(setError(error.message || 'Failed to get users'));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Register user action (admin only)
export const registerUserAction = (userData, clientId = null, user = null) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    dispatch(setError(null));
    
    const response = await registerUser(userData, clientId, user);
    
    if (response.success) {
      const newUser = response.data;
      
      // Add new user to allUsers list
      dispatch(addUser(newUser));
      
      console.log("User registered successfully", { 
        userId: newUser.id || newUser._id,
        username: newUser.username,
        role: newUser.role
      });
      
      return response;
    } else {
      throw new Error(response.message || 'Failed to register user');
    }
  } catch (error) {
    console.error("Failed to register user:", error);
    dispatch(setError(error.message || 'Failed to register user'));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Update user role action (admin only)
export const updateUserRoleAction = (userId, role, clientId = null, user = null) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    dispatch(setError(null));
    
    const response = await updateUserRole(userId, role, clientId, user);
    
    if (response.success) {
      // Update user in allUsers list
      dispatch(updateUserInList({ userId, updates: { role } }));
      
      console.log("User role updated successfully", { userId, role });
      return response;
    } else {
      throw new Error(response.message || 'Failed to update user role');
    }
  } catch (error) {
    console.error("Failed to update user role:", error);
    dispatch(setError(error.message || 'Failed to update user role'));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Toggle user status action (admin only)
export const toggleUserStatusAction = (userId, clientId = null, user = null) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    dispatch(setError(null));
    
    const response = await toggleUserStatus(userId, clientId, user);
    
    if (response.success) {
      // Update user status in allUsers list
      dispatch(updateUserInList({ userId, updates: { is_active: !response.data.is_active } }));
      
      console.log("User status toggled successfully", { userId });
      return response;
    } else {
      throw new Error(response.message || 'Failed to toggle user status');
    }
  } catch (error) {
    console.error("Failed to toggle user status:", error);
    dispatch(setError(error.message || 'Failed to toggle user status'));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Request password reset action
export const requestPasswordResetAction = (email, user = null) => async (dispatch) => {
  try {
    dispatch(setPasswordResetLoading(true));
    dispatch(setPasswordResetError(null));
    
    const response = await requestPasswordReset(email, user);
    
    if (response.success) {
      console.log("Password reset request sent successfully");
      return response;
    } else {
      throw new Error(response.message || 'Failed to request password reset');
    }
  } catch (error) {
    console.error("Password reset request failed:", error);
    dispatch(setPasswordResetError(error.message || 'Failed to request password reset'));
    throw error;
  } finally {
    dispatch(setPasswordResetLoading(false));
  }
};

// Reset password action
export const resetPasswordAction = (token, newPassword, user = null) => async (dispatch) => {
  try {
    dispatch(setPasswordResetLoading(true));
    dispatch(setPasswordResetError(null));
    
    const response = await resetPassword(token, newPassword, user);
    
    if (response.success) {
      console.log("Password reset successfully");
      return response;
    } else {
      throw new Error(response.message || 'Failed to reset password');
    }
  } catch (error) {
    console.error("Password reset failed:", error);
    dispatch(setPasswordResetError(error.message || 'Failed to reset password'));
    throw error;
  } finally {
    dispatch(setPasswordResetLoading(false));
  }
};

// Change password action
export const changePasswordAction = (currentPassword, newPassword, clientId = null, user = null) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    dispatch(setError(null));
    
    const response = await changePassword(currentPassword, newPassword, clientId, user);
    
    if (response.success) {
      console.log("Password changed successfully");
      return response;
    } else {
      throw new Error(response.message || 'Failed to change password');
    }
  } catch (error) {
    console.error("Password change failed:", error);
    dispatch(setError(error.message || 'Failed to change password'));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Update user profile action
export const updateUserProfileAction = (profileData, clientId = null, user = null) => async (dispatch) => {
  try {
    dispatch(setProfileUpdateLoading(true));
    dispatch(setProfileUpdateError(null));
    
    const response = await updateUserProfile(profileData, clientId, user);
    
    if (response.success) {
      const updatedUser = response.data;
      
      // Update user data in Redux
      dispatch(setUserData(updatedUser));
      
      // Update user in allUsers list if it exists
      dispatch(updateUserInList({ 
        userId: updatedUser.id || updatedUser._id, 
        updates: updatedUser 
      }));
      
      console.log("User profile updated successfully", { 
        userId: updatedUser.id || updatedUser._id 
      });
      
      return response;
    } else {
      throw new Error(response.message || 'Failed to update user profile');
    }
  } catch (error) {
    console.error("User profile update failed:", error);
    dispatch(setProfileUpdateError(error.message || 'Failed to update user profile'));
    throw error;
  } finally {
    dispatch(setProfileUpdateLoading(false));
  }
};

// Initialize auth state action
export const initializeAuthAction = (clientId = null, user = null) => async (dispatch) => {
  try {
    console.log("🔄 Initializing auth state...");
    
    // Add timeout to prevent hanging
    const initTimeout = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Auth initialization timeout')), 8000)
    );
    
    const initProcess = async () => {
      // Check if there's a stored token
      console.log("🔍 Checking for stored token...");
      const token = await getAuthToken();
      console.log("🔍 Token found:", !!token);
      
      const storedUser = user || await getAuthUser();
      console.log("🔍 Stored user found:", !!storedUser);
      
      if (token && storedUser) {
        console.log("🔍 Found stored auth data, validating...");
        
        try {
          // Add timeout to token validation
          console.log("🔍 Starting token validation...");
          const validationTimeout = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Token validation timeout')), 5000)
          );
          
          const validationPromise = validateToken(clientId, storedUser);
          const validationResponse = await Promise.race([validationPromise, validationTimeout]);
          
          console.log("🔍 Token validation response:", validationResponse);
          
          if (validationResponse.success) {
            console.log("✅ Stored token is valid, setting auth data");
            dispatch(setAuthData({ token, user: storedUser }));
            
            // Fetch client list for super admin users
            if (storedUser.role === 'super_admin') {
              console.log("🔍 Fetching client list for super admin...");
              try {
                const clientListResponse = await getClientList(clientId, storedUser, {});
                console.log("🔍 Client list fetched successfully", clientListResponse);
                if (clientListResponse.success) {
                  dispatch(setAvailableClients(clientListResponse.data));
                  dispatch(setCurrentClient(clientListResponse.data[0]));
                }
              } catch (clientError) {
                console.warn("⚠️ Failed to fetch client list:", clientError);
                // Don't fail initialization if client fetch fails
              }
            }
          } else {
            console.log("❌ Stored token is invalid, clearing auth data");
            await clearAuthCookies();
            dispatch(clearAuth());
          }
        } catch (error) {
          console.log("❌ Token validation failed, clearing auth data:", error);
          await clearAuthCookies();
          dispatch(clearAuth());
        }
      } else {
        console.log("ℹ️ No stored auth data found");
        dispatch(clearAuth());
      }
    };
    
    // Race between initialization and timeout
    await Promise.race([initProcess(), initTimeout]);
    
    console.log("✅ Auth initialization complete");
    dispatch(setInitialized(true));
  } catch (error) {
    console.error("❌ Auth initialization failed:", error);
    dispatch(setError(error.message));
    dispatch(setInitialized(true)); // Always set initialized to prevent infinite loading
  }
};

// Add missing exports for backward compatibility
export { getAllUsersAction as getAllUsers };
export { getUserRolesAction as getRoles };

